import React, {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {Link} from "react-router-dom";
import {OpenInNew} from "@mui/icons-material";
import {SvgIcon, Tooltip} from "@mui/material";
import {DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState,} from "../../../../../../../../../packages/data-grid";
import {VendorPurchaseHistoryDataGridCellKeys} from "../../../../../../../../../core/constants/table/data-grid-column-keys";
import PartnerDataGrid from "../../../../../../../../containers/data-grid";
import IconButton from "../../../../../../../../components/base/button/icon_button";
import {formatMoney} from "../../../../../../../../../core/services/utils/utils";
import {routeFunctions} from "../../../../../../../../../core/models/static/routes/app-routes";
import useAccessLevels from "../../../../../../../../hooks/use-access-levels";
import {ReactComponent as Printer} from "../../../../../../../../../assets/images/printer.svg";

/**
 * Purchase history tab PartnerDataGrid section.
 */
const PurchaseHistoryListHistorySection = ({
                                               data,
                                               searchData,
                                               dataGridSaveConfig,
                                               navigateToHistory,
                                               defaultVendor,
                                           }) => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.purchases.historyList'});
    const operatorAccessLevels = useAccessLevels();
    const canPrint = operatorAccessLevels.packages.bizPrint.printPurchaseHistory;

    const columns = useMemo(() => /** @type {DataGridColumn[]} */ ([
        {
            name: VendorPurchaseHistoryDataGridCellKeys.navigate,
            type: DataGridColumnTypes.element,
            alignment: DataGridColumnAlignments.center,
            sortable: false,
            width: 0,
            pinned: true,
        },
        ...(canPrint ? [{
            name: VendorPurchaseHistoryDataGridCellKeys.print,
            type: DataGridColumnTypes.element,
            alignment: DataGridColumnAlignments.center,
            sortable: false,
            width: 0,
            pinned: true,
            pinnedToggleable: false,
            resizable: false,
            visibilityToggleable: false,
        }] : []),
        {
            title: t('columns.historyNo'),
            name: VendorPurchaseHistoryDataGridCellKeys.historyNo,
            type: DataGridColumnTypes.string,
            alignment: DataGridColumnAlignments.left,
        },
        {
            title: t('columns.date'),
            name: VendorPurchaseHistoryDataGridCellKeys.date,
            type: DataGridColumnTypes.string,
            alignment: DataGridColumnAlignments.left,
        },
        {
            title: t('columns.status'),
            name: VendorPurchaseHistoryDataGridCellKeys.status,
            type: DataGridColumnTypes.string,
            alignment: DataGridColumnAlignments.left,
        },
        {
            title: t('columns.total'),
            name: VendorPurchaseHistoryDataGridCellKeys.total,
            type: DataGridColumnTypes.string,
            alignment: DataGridColumnAlignments.right,
        },
        {
            title: t('columns.currency'),
            name: VendorPurchaseHistoryDataGridCellKeys.currency,
            type: DataGridColumnTypes.string,
            alignment: DataGridColumnAlignments.left,
        },
        ...(!defaultVendor?.id ? [{
            title: t('columns.vendor'),
            name: VendorPurchaseHistoryDataGridCellKeys.vendor,
            type: DataGridColumnTypes.string,
            alignment: DataGridColumnAlignments.left,
        }] : []),
    ]), [canPrint, defaultVendor?.id, t]);

    const [dataGridColumns, , , density] = useDataGridSavedState({
        columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    const rows = useMemo(() => data.map((row) => {
        const vendorId = defaultVendor?.id ?? row.vendor?.id ?? row.vendorId;
        const open = () => navigateToHistory(row, vendorId);
        return {
            key: row.id,
            data: row,
            cells: {
                [VendorPurchaseHistoryDataGridCellKeys.navigate]: (
                    defaultVendor?.id && vendorId
                        ? (
                            <IconButton
                                color={'secondary'}
                                component={Link}
                                to={routeFunctions.vendor.purchases.history.single(vendorId, row.id)}
                            >
                                <OpenInNew className={'icon normal'}/>
                            </IconButton>
                        )
                        : (
                            <IconButton color={'secondary'} onPressed={open}>
                                <OpenInNew className={'icon normal'}/>
                            </IconButton>
                        )
                ),
                ...(canPrint && vendorId ? {
                    [VendorPurchaseHistoryDataGridCellKeys.print]: (
                        <Tooltip arrow title={t('actions.print')}>
                            <IconButton
                                color={'secondary'}
                                component={Link}
                                target={'_blank'}
                                to={routeFunctions.print.purchaseHistory(vendorId, row.id)}
                                aria-label={t('actions.print')}
                            >
                                <SvgIcon className={'icon normal'} component={Printer}/>
                            </IconButton>
                        </Tooltip>
                    ),
                } : {}),
                [VendorPurchaseHistoryDataGridCellKeys.historyNo]: row.historyNo,
                [VendorPurchaseHistoryDataGridCellKeys.date]: row.date,
                [VendorPurchaseHistoryDataGridCellKeys.status]: row.status,
                [VendorPurchaseHistoryDataGridCellKeys.total]: formatMoney(row.total),
                [VendorPurchaseHistoryDataGridCellKeys.currency]: row.currency ?? 'CAD',
                [VendorPurchaseHistoryDataGridCellKeys.vendor]:
                    row.vendor?.name ?? row.vendor?.title ?? '',
            },
        };
    }), [canPrint, data, defaultVendor?.id, navigateToHistory, t]);

    return (
        <PartnerDataGrid
            {...dataGridSaveConfig}
            initialLayoutState={{columns}}
            columns={dataGridColumns}
            rows={rows}
            hideFooterActions
            hideExporting
            state={{
                pagination: searchData.paginationInfo,
                sortBy: searchData.orderBy,
                loading: {state: searchData.loading},
                density,
            }}
            onSortByChanged={(_, current) => searchData.setOrderBy(current)}
            onCurrentPageChanged={(_, current) => searchData.setPaginationInfo((prev) => ({...prev, ...current}))}
            onPageSizeChanged={(_, current) => searchData.setPaginationInfo((prev) => ({...prev, ...current}))}
        />
    );
};

export default PurchaseHistoryListHistorySection;
